/* eslint-disable no-restricted-properties */
import io from 'socket.io-client';
import ms from 'ms';
import moment from 'moment';

import { appTypes, baseEndpoint, companyPrefix, userAccountTypes } from 'shared/constants';
import { isAgentIntersect, getParsedUserByAppType, logError, logDebug } from 'shared/functions';

const validPeriod = (eventMoment) => eventMoment && !eventMoment.diff(moment(), 'minutes');

export const getConnectedSocket = (self, appType) => {
  const { accountTypes = [] } = getParsedUserByAppType(appType);
  const userAccountTypeIntersect =
    appType === appTypes.candidate ? accountTypes.includes(userAccountTypes.candidate) : isAgentIntersect(accountTypes);
  if (!userAccountTypeIntersect) {
    logError(accountTypes);
    return logError(`Unable to connect ${appType} socket`);
  }

  const socket = io(`${baseEndpoint}/${companyPrefix}-${appType}`, {
    path: `/messenger-${appType}`,
    // @ts-expect-error
    query: `companyId=${companyPrefix}&jwt=${localStorage.token}`,
    reconnection: true,
    reconnectionDelay: ms('1s'),
    reconnectionDelayMax: ms('5s'),
    reconnectionAttempts: 180,
  });

  const skippedEvents = ['online', 'ping', 'pong'];
  // @ts-expect-error
  const onevent = socket.onevent;
  // @ts-expect-error
  socket.onevent = (packet) => {
    const { data = [] } = packet;
    if (data[1]) {
      const { eventTime } = data[1];
      if (eventTime) {
        self.lastEventTime = {
          eventTime,
          eventMoment: moment(),
        };
      }
    }
    if (localStorage.debugSocket) {
      logDebug(`< < < *** ${data[0]} ***`);
      if (data[1]) {
        logDebug(data[1]);
      }
    }
    if (data[0] === 'unauthorized') {
      self.setState({
        socket: null,
      });
      self.props.dispatchSignOut();
    }
    onevent.call(socket, packet);
  };
  const emit = socket.emit;
  // @ts-expect-error
  socket.emit = (event, packet) => {
    if (localStorage.debugSocketOutgoing) {
      if (!skippedEvents.includes(event)) {
        logDebug(`> > > *** ${event} ***`);
        if (packet) {
          logDebug(packet);
        }
      }
    }
    emit.call(socket, event, packet);
  };

  ['connect_failed', 'connect_error', 'connect_timeout', 'reconnect_failed'].map((eventType) =>
    socket.on(eventType, (error) => {
      self.disconnectMoment = moment();
      self.setState({
        disconnected: true,
        connectionCanBeRestored: false,
        disconnectReason: error,
      });
    }),
  );

  socket
    .on('connect', () => {
      const { connectionCanBeRestored } = self.state;

      if (self.unmounted) {
        return;
      }

      // Reload events
      if (self.lastEventTime) {
        const { eventTime, eventMoment } = self.lastEventTime || {};
        socket.emit('reload-events', {
          lastEventTime: validPeriod(eventMoment) ? eventTime : null,
        });
      }

      self.setState(
        connectionCanBeRestored && self.state.socket
          ? {
              disconnected: false,
            }
          : {
              socket,
            },
      );

      // Initial connect events
      if (!self.socketEventsConnected) {
        self.connectSocketEvents(socket);
      }
    })
    .on('disconnect', (reason) => {
      if (self.unmounted) {
        return;
      }

      self.disconnectMoment = moment();
      self.setState({
        disconnected: true,
        connectionCanBeRestored: true,
        disconnectReason: reason,
      });

      setTimeout(
        () => {
          const { connectionCanBeRestored } = self.state;

          if (connectionCanBeRestored !== false && !validPeriod(self.disconnectMoment)) {
            self.setState({
              connectionCanBeRestored: false,
            });
          }
        },
        ms('1m') + ms('1s'),
      );
    })
    .on('single-logout', ({ userId }) => {
      if (userId && userId === self.props?.user?.userId) {
        self.setState({ socket: null });
        self.props.dispatchSignOut();
      }
    });

  return socket;
};
